import React, { useState } from 'react';
import { kitchenService } from '../../services';
import { toast } from 'react-toastify';

interface Form7Props {
  onNext: (data: Form7Data) => void;
  initialData?: Form7Data;
}

export interface Form7Data {
  accountHolderName: string;
  bankName: string;
  accountNumber: string;
  ifscCode: string;
  upiId: string;
  settlementCycle: string;
}

const Form7: React.FC<Form7Props> = ({ onNext, initialData }) => {
  const [formData, setFormData] = useState<Form7Data>({
    accountHolderName: initialData?.accountHolderName || '',
    bankName: initialData?.bankName || 'Select Your Bank',
    accountNumber: initialData?.accountNumber || '',
    ifscCode: initialData?.ifscCode || '',
    upiId: initialData?.upiId || '',
    settlementCycle: initialData?.settlementCycle || 'Select Settlement Cycle',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const bankOptions = [
    'Select Your Bank',
    'State Bank of India',
    'HDFC Bank',
    'ICICI Bank',
    'Axis Bank',
    'Kotak Mahindra Bank',
    'Punjab National Bank',
    'Bank of Baroda',
    'Canara Bank',
    'Union Bank of India',
    'Bank of India',
    'IDBI Bank',
    'Central Bank of India',
    'Indian Bank',
    'UCO Bank',
    'Yes Bank',
    'IndusInd Bank',
    'IDFC First Bank',
    'Federal Bank',
    'RBL Bank',
  ];

  const settlementOptions = [
    'Select Settlement Cycle',
    'Daily',
    'Weekly',
    'Bi-Weekly',
    'Monthly',
  ];

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.accountHolderName.trim()) {
      setError('Account Holder Name is required');
      return;
    }
    if (!formData.bankName || formData.bankName === 'Select Your Bank') {
      setError('Please select a bank');
      return;
    }
    if (!formData.accountNumber.trim()) {
      setError('Account Number is required');
      return;
    }
    if (!/^[0-9]{9,18}$/.test(formData.accountNumber.trim())) {
      setError('Please enter a valid account number (9-18 digits)');
      return;
    }
    if (!formData.ifscCode.trim()) {
      setError('IFSC Code is required');
      return;
    }
    // if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode.trim().toUpperCase())) {
    //   setError('Please enter a valid IFSC Code (e.g., HDFC0001234)');
    //   return;
    // }
    if (!formData.settlementCycle || formData.settlementCycle === 'Select Settlement Cycle') {
      setError('Please select a settlement cycle');
      return;
    }

    setLoading(true);
    try {
      // Submit to API
      const response: any = await kitchenService.submitStep7(formData);
      toast.success(response.message || 'Bank details saved successfully!');
      // Move to next step
      onNext(formData);
    } catch (err: any) {
      setError(err.message || 'Failed to save data. Please try again.');
      toast.error(err.message || 'Failed to save bank details');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="">
      <div className="w-full">
        <h2 className="text-[35px] font-Urbanist font-semibold text-gray-900 mb-2 text-left leading-[125%]">
          Bank & Settlement Details
        </h2>
        <p className="text-[#6A6A6A] text-[15px] font-medium leading-[140%]">
          Enter these details to get started
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5 mt-[20px]">
          {/* Account Holder Name */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="accountHolderName"
              className="text-sm font-Urbanist font-medium text-[#6A6A6A]"
            >
              Account Holder Name
            </label>
            <input
              type="text"
              id="accountHolderName"
              name="accountHolderName"
              value={formData.accountHolderName}
              onChange={handleChange}
              placeholder="FULL NAME"
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all uppercase"
              required
            />
          </div>

          {/* Bank Name */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="bankName"
              className="text-sm font-Urbanist font-medium text-[#6A6A6A]"
            >
              Bank Name
            </label>
            <select
              id="bankName"
              name="bankName"
              value={formData.bankName}
              onChange={handleChange}
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all cursor-pointer"
              required
            >
              {bankOptions.map((option) => (
                <option key={option} value={option} disabled={option === 'Select Your Bank'}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {/* Account Number */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="accountNumber"
              className="text-sm font-Urbanist font-medium text-[#6A6A6A]"
            >
              Account Number
            </label>
            <input
              type="text"
              id="accountNumber"
              name="accountNumber"
              value={formData.accountNumber}
              onChange={handleChange}
              placeholder="ENTER ACCOUNT NUMBER"
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all uppercase"
              required
            />
          </div>

          {/* IFSC Code */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="ifscCode"
              className="text-sm font-Urbanist font-medium text-[#6A6A6A]"
            >
              IFSC Code
            </label>
            <input
              type="text"
              id="ifscCode"
              name="ifscCode"
              value={formData.ifscCode}
              onChange={handleChange}
              placeholder="ENTER IFSC CODE"
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all uppercase"
              required
              maxLength={11}
            />
          </div>

          {/* UPI ID */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="upiId"
              className="text-sm font-Urbanist font-medium text-[#6A6A6A]"
            >
              UPI ID
            </label>
            <input
              type="text"
              id="upiId"
              name="upiId"
              value={formData.upiId}
              onChange={handleChange}
              placeholder="Enter your UPI ID"
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all"
            />
          </div>

          {/* Settlement Cycle */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="settlementCycle"
              className="text-sm font-Urbanist font-medium text-[#6A6A6A]"
            >
              Settlement Cycle
            </label>
            <select
              id="settlementCycle"
              name="settlementCycle"
              value={formData.settlementCycle}
              onChange={handleChange}
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all cursor-pointer"
              required
            >
              {settlementOptions.map((option) => (
                <option key={option} value={option} disabled={option === 'Select Settlement Cycle'}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm mt-2">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 mt-2">
            <button
              type="submit"
              className="w-full px-6 py-3.5 text-base font-Urbanist font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-400 rounded-lg hover:from-orange-600 hover:to-orange-500 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 active:scale-[0.98]"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Next'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Form7;